/**
 * Hook for loading user's media jobs (videos and images)
 */

import { useRef } from "react"

import { useQuery } from "@tanstack/react-query"

import type { Image, MediaJob } from "@/features/studio-ai/types/studio-ai.types"

import { listUserJobs } from "@/api/studio-ai-api"
import { MediaType } from "@/features/studio-ai/types/studio-ai.types"
import { isActiveMediaStatus } from "@/features/studio-ai/utils/media-status-utils"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ImageCacheService } from "@/services/image-cache-service"
import { useStudioAIStore } from "@/stores/studio-ai-store"

export function useUserJobs(mediaType?: string) {
  const setJobs = useStudioAIStore((state) => state.setJobs)
  const hasInitialized = useRef(false)

  return useQuery({
    queryKey: queryKeys.sora2.jobs(mediaType),
    queryFn: async () => {
      logger.info("Loading user jobs from API...")
      try {
        // Cleanup expired cache entries first
        ImageCacheService.cleanupExpired()

        // Fetch jobs directly from API
        const jobs = await listUserJobs(undefined, mediaType)

        const cachedImages = ImageCacheService.getImages(
          jobs
            .filter((job) => job.media_type === MediaType.Image && !(job as Image).image_url)
            .map((job) => job.job_id)
        )

        // Restore image_url from cache for image jobs
        const jobsWithCache: MediaJob[] = jobs.map((job) => {
          if (job.media_type === MediaType.Image) {
            const imageJob = job as Image
            if (!imageJob.image_url) {
              const cachedImage = cachedImages.get(job.job_id)
              if (cachedImage) {
                return { ...imageJob, image_url: cachedImage } as Image
              }
            }
          }
          return job
        })

        // Merge with existing store jobs: preserve image_url from store if API returns null
        const currentJobs = useStudioAIStore.getState().jobs
        const currentJobsById = new Map(currentJobs.map((job) => [job.job_id, job]))
        const mergedJobs = jobsWithCache.map((job) => {
          if (job.media_type === MediaType.Image && !(job as Image).image_url) {
            const existing = currentJobsById.get(job.job_id) as Image | undefined
            if (existing?.image_url) {
              return { ...job, image_url: existing.image_url } as Image
            }
          }
          return job
        })

        // Update store for UI state (videos/images arrays)
        setJobs(mergedJobs)

        logger.info(`Loaded ${mergedJobs.length} jobs from API with cache restoration`)
        hasInitialized.current = true
        return mergedJobs
      } catch (error) {
        logger.error("Failed to load user jobs", error as Error)
        throw error
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity, // Never consider data stale automatically
    gcTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchInterval: (query) => {
      // Only start polling after initial load completes
      if (!hasInitialized.current) return false

      // Only poll if there are active jobs
      const jobs = query.state.data || []
      const hasActiveJobs = jobs.some((job) => isActiveMediaStatus(job.status))
      // Poll every 15 seconds if active jobs exist, otherwise stop polling
      return hasActiveJobs ? 15000 : false
    },
    retry: 1,
  })
}
